import React, { useEffect, useId, useRef, useState } from 'react';
import { COPY } from './copy';

const TITLE_ID = 'rai-composer-confirm-title';

const ConfirmModal = ({ open, onCancel, onConfirm, submitting }) => {
  const cancelBtnRef = useRef(null);
  const previousActiveRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previousActiveRef.current = document.activeElement;
    cancelBtnRef.current?.focus();

    const handler = (e) => { if (e.key === 'Escape' && !submitting) onCancel(); };
    document.addEventListener('keydown', handler);

    return () => {
      document.removeEventListener('keydown', handler);
      previousActiveRef.current?.focus?.();
    };
  }, [open, submitting, onCancel]);

  if (!open) return null;

  const handleBackdrop = () => {
    if (submitting) return;
    onCancel();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      onClick={handleBackdrop}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1001,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 8, padding: 24, maxWidth: 480, width: '90%' }}>
        <h2 id={TITLE_ID} className="text-lg font-semibold mb-4">Please confirm.</h2>
        <div className="flex flex-row gap-x-3 justify-end">
          <button ref={cancelBtnRef} type="button" onClick={onCancel} disabled={submitting} className="btn px-4 py-2 rounded border border-gray-400">
            {COPY.buttons.cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="btn font-semibold text-white px-4 py-2 rounded"
            style={{ backgroundColor: COPY.hex.alertRed }}
          >
            {COPY.buttons.send}
          </button>
        </div>
      </div>
    </div>
  );
};

const MessageComposer = ({ onSubmit, placeholder = 'Enter the requested information', submittingHint = false }) => {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const generatedId = useId();
  const bodyId = `rai-composer-body-${generatedId}`;
  const filesId = `rai-composer-files-${generatedId}`;

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSendClick = () => {
    if (!body.trim() && files.length === 0) {
      setError('Please enter information or upload a file.');
      return;
    }
    setError(null);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ body: body.trim(), files });
      setBody('');
      setFiles([]);
      setConfirmOpen(false);
    } catch (err) {
      const msg = err.response?.data?.errors?.join(', ') || 'Failed to send message';
      setError(msg);
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-y-2">
      <label htmlFor={bodyId} className="font-semibold text-slate-700">{COPY.sections.additionalInformationLabel}</label>
      <textarea
        id={bodyId}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        style={{ minHeight: 120, padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
      />

      <div className="flex flex-col gap-y-1">
        <label htmlFor={filesId} className="font-semibold text-slate-600 uppercase text-sm">Upload File</label>
        <input
          id={filesId}
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
        />
        {files.length > 0 && (
          <ul className="text-sm mt-1">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex flex-row items-center gap-x-2">
                <span>{f.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-red-600 underline text-xs"
                  disabled={submitting}
                >
                  remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="flex flex-row justify-center mt-1">
        <button
          type="button"
          onClick={handleSendClick}
          disabled={submitting || submittingHint}
          className="btn font-semibold text-white px-4 py-2 rounded"
          style={{ backgroundColor: COPY.hex.alertRed }}
        >
          {COPY.buttons.send}
        </button>
      </div>

      <ConfirmModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        submitting={submitting}
      />
    </div>
  );
};

export default MessageComposer;
